import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  CodeExceptionStatus,
  PassOrderPageData,
  PassOut,
  PassScheduleSelectionDTO,
  PassService,
  PassType,
} from '../../../../api';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { useApiResponseHandlerContext } from '../../../../common/apiNotifications/useApiResponseHandlerContext';
import { ApiResponseHandlerType } from '../../../../common/apiNotifications/ApiResponseHandlerContext';
import { FormDatePicker } from '../../../../experienceSettings/formComponents/FormDatePicker';
import { DATE_FORMAT } from '../../../../calendar/calendarWrapper/CalendarWrapper';
import { FixedSchedulePicker } from './FixedSchedulePicker';

type Props = {
  open: boolean;
  onClose: () => void;
  pass: PassOut;
  passOrderPage: PassOrderPageData;
  onUpdated: (pass: PassOut) => void;
};

export const ChangeScheduleDialog = ({ open, onClose, pass, passOrderPage, onUpdated }: Props) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as { id: string };
  const { showResponseNotification } = useApiResponseHandlerContext();

  const isFixed = passOrderPage.pass_type === PassType.FIXED_SCHEDULE_MONTHLY;
  const minCount = isFixed ? passOrderPage.weekly_session_count ?? 0 : 1;
  const maxCount = isFixed ? passOrderPage.weekly_session_count ?? 0 : Infinity;
  const isUnbounded = !Number.isFinite(maxCount);

  const [effectiveFrom, setEffectiveFrom] = useState<string>(moment().format(DATE_FORMAT));
  const [selections, setSelections] = useState<PassScheduleSelectionDTO[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const isOutOfRange = (count: number): boolean => count < minCount || (!isUnbounded && count > maxCount);
  const countMismatchMessage = (got: number): string =>
    isFixed
      ? t('passes.fixed_schedule_errors.schedule_count_mismatch', { expected: minCount, got })
      : t('passes.fixed_schedule_errors.schedule_selections_required');

  useEffect(() => {
    if (!open) return;
    setSelections([]);
    setEffectiveFrom(moment().format(DATE_FORMAT));
    setInlineError(null);
  }, [open, passOrderPage.id]);

  const handleSubmit = async () => {
    if (isOutOfRange(selections.length)) {
      setInlineError(countMismatchMessage(selections.length));
      return;
    }
    setSubmitting(true);
    setInlineError(null);
    try {
      const updated = await PassService.updatePassSchedule(pass.id, {
        effective_from: effectiveFrom,
        schedule_selections: selections,
      });
      onUpdated(updated);
      onClose();
    } catch (e: any) {
      const code = e?.body?.code as CodeExceptionStatus | undefined;
      const detail = e?.body?.detail as string | undefined;
      if (code === CodeExceptionStatus.PASS_SLOT_AT_CAPACITY) {
        showResponseNotification(t('passes.fixed_schedule_errors.slot_at_capacity'), ApiResponseHandlerType.ERROR);
        setSelections([]);
        setRefreshKey((k) => k + 1);
      } else if (detail?.startsWith('scheduleSelectionsCountMismatch')) {
        setInlineError(countMismatchMessage(selections.length));
      } else if (detail === 'fixedScheduleMonthlyMissingWeeklySessionCount') {
        showResponseNotification(
          t('passes.fixed_schedule_errors.template_missing_weekly_count'),
          ApiResponseHandlerType.ERROR,
        );
      } else if (detail === 'scheduledWeeklyRequiresEventTarget') {
        showResponseNotification(t('passes.fixed_schedule_errors.event_target_required'), ApiResponseHandlerType.ERROR);
      } else {
        showResponseNotification(t('saveHeader.errorGeneric'), ApiResponseHandlerType.ERROR);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('passes.change_schedule_dialog_title')}</DialogTitle>
      <DialogContent>
        <Grid container flexDirection="column" gap={2}>
          <Box>
            {/* Formik only wraps the date field; the slot picker keeps its own state. */}
            <Formik initialValues={{ effective_from: effectiveFrom }} enableReinitialize onSubmit={() => {}}>
              <Form>
                <FormDatePicker
                  fieldName="effective_from"
                  label={t('passes.change_schedule_effective_from')}
                  onChange={(value) => value && setEffectiveFrom(value.format(DATE_FORMAT))}
                  isFieldUpdate={false}
                  size="small"
                />
              </Form>
            </Formik>
          </Box>

          {inlineError && <Alert severity="error">{inlineError}</Alert>}

          <FixedSchedulePicker
            passOrderPage={passOrderPage}
            accountId={account.id}
            value={selections}
            onChange={(sel) => {
              setInlineError(null);
              setSelections(sel);
            }}
            refreshKey={refreshKey}
            minSelections={minCount}
            maxSelections={maxCount}
          />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('common.cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSubmit}
          loading={submitting}
          disabled={isOutOfRange(selections.length)}>
          {t('save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
